/**
 * Deterministic search classification shared by Catalog (backend) and Collection.
 * Keep in sync with apps/backend/src/catalog/catalog-search.ts parseCollectorNumberQuery.
 *
 * - bare digits → collector number, never name substring
 * - N/M with optional spaces → collector number + total
 * - any other non-empty string → text
 */

export type ClassifiedSearchQuery =
  | { kind: "empty" }
  | { kind: "collector"; collectorNumber: number; collectorTotal: number | null }
  | { kind: "text"; normalized: string };

function parsePositiveInt(raw: string): number | null {
  if (!/^\d+$/.test(raw)) {
    return null;
  }
  const value = Number(raw);
  if (!Number.isInteger(value) || !Number.isSafeInteger(value) || value <= 0) {
    return null;
  }
  return value;
}

export function normalizeSearchText(value: string): string {
  return value.normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase();
}

export function classifySearchQuery(raw: string): ClassifiedSearchQuery {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return { kind: "empty" };
  }

  const fraction = /^(\d+)\s*\/\s*(\d+)$/.exec(trimmed);
  if (fraction) {
    const collectorNumber = parsePositiveInt(fraction[1] ?? "");
    const collectorTotal = parsePositiveInt(fraction[2] ?? "");
    if (collectorNumber !== null && collectorTotal !== null) {
      return { kind: "collector", collectorNumber, collectorTotal };
    }
  }

  if (/^\d+$/.test(trimmed)) {
    const collectorNumber = Number(trimmed);
    if (!Number.isInteger(collectorNumber) || !Number.isSafeInteger(collectorNumber)) {
      return { kind: "collector", collectorNumber: 0, collectorTotal: null };
    }
    return { kind: "collector", collectorNumber, collectorTotal: null };
  }

  return { kind: "text", normalized: normalizeSearchText(trimmed) };
}
